import { PartnerOrder } from '../../../api/partner/partnerOfferService';
import { PartnerOrderData } from './types';
import { STATUS_LABELS, getStatusLabels } from './constants';
import { transformPlatform } from '../../../utils';

export const transformStatus = (
  status: string
): 'in_progress' | 'not_started' | 'waiting' | 'canceled' | 'completed' => {
  switch (status) {
    case 'in_progress':
      return 'in_progress';
    case 'not_started':
      return 'not_started';
    case 'waiting':
      return 'waiting';
    case 'completed':
      return 'completed';
    case 'canceled':
      return 'canceled';
    default:
      return 'waiting';
  }
};

const toNumber = (value: any): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

// Map work_content category keys to display labels
const getCategoryLabel = (category: string): string => {
  const labels: Record<string, string> = {
    nonude: 'No nude',
    tt: 'TikTok',
    lingerie: 'Lingerie',
    lite_nude: 'Lite nude',
    full_nude: 'Full nude',
  };
  return labels[category] || category;
};

export const transformPartnerOrderToOrderData = (partnerOrder: PartnerOrder): PartnerOrderData => {
  // Статистика транзакций
  const transactionStats: any = partnerOrder.transactions_statistic || {};
  const totalTransactions = toNumber(transactionStats?.total);

  // Подписчики
  const totalSubscribers = partnerOrder.subs.count || 0;
  const verifiedUsers = partnerOrder.subscribed_count || 0;
  const subscribersWithMessages = partnerOrder.subs.with_gte_3_chat_messages_count || 0;

  // Расчет ARPU (средний доход на пользователя)
  const arpu = verifiedUsers > 0 ? parseFloat((totalTransactions / verifiedUsers).toFixed(2)) : 0;

  // Расчет ROMI (возврат на инвестиции в маркетинг)
  const rawCommission = toNumber(partnerOrder.commition);
  const commissionPercent = rawCommission <= 1 ? rawCommission * 100 : rawCommission;
  const totalCostWithCommission = toNumber(partnerOrder.total_price);
  const baseCost =
    commissionPercent !== 0
      ? parseFloat((totalCostWithCommission / (1 + commissionPercent / 100)).toFixed(2))
      : totalCostWithCommission;
  const commissionAmount = Math.max(parseFloat((totalCostWithCommission - baseCost).toFixed(2)), 0);
  const revenue = totalTransactions;

  const romi =
    totalCostWithCommission > 0
      ? parseFloat(
          (((revenue - totalCostWithCommission) / totalCostWithCommission) * 100).toFixed(2)
        )
      : 0;

  return {
    ...partnerOrder,
    id: partnerOrder.id,
    internalId: partnerOrder.id.slice(-4),
    model: {
      id: partnerOrder.model.id,
      link: partnerOrder.model.link,
      name: partnerOrder.model.of_user_name,
      username: partnerOrder.model.of_user_name,
      avatar: partnerOrder.model.avatar || undefined,
      type: partnerOrder.model.type,
      overall_popularity_percent: partnerOrder.model.overall_popularity_percent || 0,
    },
    content: {
      mediaUrls: partnerOrder.model.public_content || [],
      contentLinks: partnerOrder.model.work_content
        ? Object.entries(partnerOrder.model.work_content)
            .filter(([_, url]) => url && String(url).trim() !== '')
            .map(([category, url]) => ({
              category: getCategoryLabel(category),
              url: url as string,
            }))
        : [],
    },
    offer: {
      id: partnerOrder.offer.id,
      partner: {
        id: partnerOrder.offer.partner.id,
        name: partnerOrder.offer.partner.name,
        avatar: partnerOrder.offer.partner.avatar || undefined,
      },
      platform: transformPlatform(partnerOrder.offer.source),
      description: partnerOrder.offer.description,
      arpu: arpu,
      romi: romi,
    },
    blocked_countries: partnerOrder.model.blocked_countries,
    campaign: {
      link: partnerOrder.model.link,
      transitions_count: partnerOrder.model.transitions_count || 0,
    },
    subscribers: {
      count: partnerOrder.subs.count,
      price: parseFloat(partnerOrder.subs.price),
      withMessages: subscribersWithMessages,
    },
    commition: commissionPercent,
    statistics: {
      totalPrice: totalCostWithCommission,
      basePrice: baseCost,
      commission: commissionPercent,
      commissionAmount,
      netRevenue: revenue,
      subscribedCount: verifiedUsers,
      transactions: {
        ...partnerOrder.transactions_statistic,
        total: totalTransactions,
        purchases: transactionStats.count || 0,
      },
      arpu: arpu,
      romi: romi,
    },
    status: transformStatus(partnerOrder.status),
    createdAt: partnerOrder.created_at,
    subscribeDays: partnerOrder.subscribe_days,
  };
};

export const getStatusLabel = (status: string, t?: (key: string) => string): string => {
  if (t) {
    const statusLabels = getStatusLabels(t);
    return statusLabels[status as keyof typeof statusLabels] || status;
  }
  return STATUS_LABELS[status as keyof typeof STATUS_LABELS] || status;
};
